import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, LayoutGrid, Settings } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { canSee } from "@/lib/access";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/brand/AppHeader";
import { MyHorses } from "@/components/seller/MyHorses";
import { TodoCard } from "@/components/seller/TodoCard";

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("email_change");
    if (!p) return;
    const msg = { done: ["success", "Your account email has been changed."], taken: ["error", "That email is already used by another account."],
      expired: ["error", "That email change link has expired. Please request a new one."], used: ["error", "That email change link was already used."] }[p] || ["error", "That email change link isn't valid."];
    toast[msg[0]](msg[1]);
    if (p === "done") refresh();
    window.history.replaceState(null, "", window.location.pathname);
  }, [refresh]);
  const navigate = useNavigate();
  const adminView = canSee(user, "pipeline");

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <AppHeader title={<><span className="hidden sm:inline lg:hidden">Dashboard</span><span className="hidden lg:inline">Seller Dashboard</span></>} home="/dashboard" width="max-w-5xl"
        eyebrow={user?.email && <span data-testid="dashboard-user-email-mobile" title={user.email} className="sm:hidden normal-case tracking-normal text-[11px]">{user.email}</span>}>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {adminView && (
              <Button data-testid="go-admin-button" variant="outline" onClick={() => navigate("/pipeline")}
                className="rounded-full border-white/30 bg-transparent text-white/85 hover:bg-white/10 hover:text-white px-2.5 sm:px-3 h-9 text-xs" aria-label="Admin Pipeline" title="Admin Pipeline">
                <LayoutGrid className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Admin Pipeline</span>
              </Button>
            )}
            <div className="hidden lg:block min-w-0 text-right lg:max-w-[14rem]" data-testid="dashboard-account">
              {user?.name && <div data-testid="dashboard-user-name" className="text-xs font-semibold text-stone-800 truncate">{user.name}</div>}
              <div data-testid="dashboard-user-email" title={user?.email} className="text-[11px] text-stone-500 truncate">{user?.email}</div>
            </div>
            <Button data-testid="settings-button" variant="outline" onClick={() => navigate("/dashboard/settings")} aria-label="Account Settings" title="Account Settings"
              className="rounded-full border-stone-300 text-stone-600 hover:bg-stone-50 h-10 w-10 p-0">
              <Settings className="h-4 w-4" />
            </Button>
            <Button data-testid="logout-button" variant="outline" onClick={logout}
              className="rounded-full border-stone-300 text-stone-600 hover:bg-stone-50 px-3 sm:px-4">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
      </AppHeader>

      <main className="max-w-6xl mx-auto px-3 sm:px-8 py-5 sm:py-8">
        <TodoCard />
        <MyHorses />
      </main>
    </div>
  );
}
